import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';


import { LocalNotifications } from '@capacitor/local-notifications';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

function AddTask() {
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('userTasks');
    return saved ? JSON.parse(saved) : [];
  });
  const [taskText, setTaskText] = useState('');
  const [priority, setPriority] = useState('normal');
  const [deadline, setDeadline] = useState(dayjs().add(1, 'day'));
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);


  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    localStorage.setItem('userTasks', JSON.stringify(tasks));
  }, [tasks]);

  // Initialize notifications on component mount
  useEffect(() => {
    initializeNotifications();
  }, []);

  const initializeNotifications = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const permission = await LocalNotifications.requestPermissions();
        if (permission.display !== 'granted') {
          console.warn('Notification permission not granted');
          return false;
        }
        
        // Check exact alarm setting for Android 12+
        try {
          const exactSetting = await LocalNotifications.checkExactNotificationSetting();
          if (exactSetting && exactSetting.exact === 'denied') {
            console.warn('Exact alarm permission denied. Notifications might be delayed.');
            // You might want to add a UI alert here later to tell user to enable it in settings
          }
        } catch (error) {
          if (error.message && !error.message.includes('Not implemented on web')) {
            console.log('Exact notification setting check error:', error);
          }
        }
        
        // Create notification channel for Android
        if (Capacitor.getPlatform() === 'android') {
          await LocalNotifications.createChannel({
            id: 'task-reminders',
            name: 'Task Reminders',
            description: 'Notifications for task deadlines and reminders',
            importance: 5, // Increased to 5 (High) for reliable delivery
            visibility: 1,
            sound: 'default',
            vibration: true,
            lights: true,
            lightColor: '#488AFF'
          });
          console.log('Task notification channel created successfully');
        }
        
        return true;
      } catch (error) {
        console.error('Error initializing notifications:', error);
        return false;
      }
    }
    return false;
  };

  const scheduleNotification = async (task) => {
    if (!Capacitor.isNativePlatform()) {
      console.log('Notifications only work on native platforms');
      return;
    }

    try {
      const notificationTime = dayjs(task.deadline).hour(12).minute(0).second(0);
      
      if (notificationTime.isBefore(dayjs())) {
        console.warn('Cannot schedule notification for past date');
        return;
      }

      const emoji = task.priority === 'important' ? '❗' : '';
      
      await LocalNotifications.schedule({
        notifications: [
          {
            title: `任務提醒 ${emoji}`,
            body: `${task.text} - 截止日期：${dayjs(task.deadline).format('ddd, DD-MMM-YYYY')}`,
            id: task.id,
            channelId: 'task-reminders',
            schedule: { 
              at: notificationTime.toDate(),
              allowWhileIdle: true // Critical for Doze mode
            },
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
            attachments: [],
            actionTypeId: '',
            extra: { taskId: task.id }
          }
        ]
      });
      
      console.log('Notification scheduled for:', notificationTime.format('YYYY-MM-DD HH:mm'));
    } catch (error) {
      console.error('Error scheduling notification:', error);
    }
  };

  const addTask = async () => {
    if (!taskText.trim()) {
      return;
    }

    const newTask = {
      id: Date.now(),
      text: taskText.trim(),
      priority,
      deadline: deadline.toISOString(),
      createdAt: dayjs().toISOString(),
      completed: false
    };

    setTasks(prev => [...prev, newTask]);
    await scheduleNotification(newTask);
    
    setTaskText('');
    setPriority('normal');
    setDeadline(dayjs().add(1, 'day'));
    setOpenDialog(false);
    

  };

  const editTask = async () => {
    if (!taskText.trim()) {
      return;
    }

    const updatedTask = {
      ...editingTask,
      text: taskText.trim(),
      priority,
      deadline: deadline.toISOString()
    };

    setTasks(prev => prev.map(task => task.id === editingTask.id ? updatedTask : task));
    
    // Cancel old notification and schedule new one
    if (Capacitor.isNativePlatform()) {
      await LocalNotifications.cancel({ notifications: [{ id: editingTask.id }] });
      await scheduleNotification(updatedTask);
    }
    
    resetForm();

  };

  const deleteTask = async (taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
    
    // Cancel notification
    if (Capacitor.isNativePlatform()) {
      await LocalNotifications.cancel({ notifications: [{ id: taskId }] });
    }
    

  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setTaskText(task.text);
    setPriority(task.priority);
    setDeadline(dayjs(task.deadline));
    setOpenDialog(true);
  };

  const resetForm = () => {
    setTaskText('');
    setPriority('normal');
    setDeadline(dayjs().add(1, 'day'));
    setEditingTask(null);
    setOpenDialog(false);
  };

  const addToGoogleCalendar = async (task) => {
    const startDate = dayjs(task.deadline).format('YYYYMMDD');
    const endDate = dayjs(task.deadline).add(1, 'day').format('YYYYMMDD');
    const title = encodeURIComponent(task.text);
    const details = encodeURIComponent(`優先級: ${task.priority === 'important' ? '重要' : '一般'}`);
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}`;
    
    try {
      await Browser.open({ url: googleCalendarUrl });

    } catch (error) {
      console.error('Calendar error:', error);
      console.error('Google Calendar error:', error);
    }
  };

  const sendAllTasksToWhatsApp = async (phoneNumber) => {
    if (tasks.length === 0) {
      console.log('No tasks to send');
      return;
    }

    const recipientIdentifier = phoneNumber === '85292892534' ? '🦸🏻🦸🏻' : '🐷🐷';
     let message = `📋 Task for ${recipientIdentifier}\n\n`;
    
    // Use the same sorting logic as the app display
      const sortedTasksForWhatsApp = [...tasks].sort((a, b) => {
        const aIsOverdue = dayjs(a.deadline).isBefore(dayjs(), 'day');
        const bIsOverdue = dayjs(b.deadline).isBefore(dayjs(), 'day');
        
        // First priority: overdue tasks come first
        if (aIsOverdue && !bIsOverdue) return -1;
        if (!aIsOverdue && bIsOverdue) return 1;
        
        // Second priority: sort by deadline (nearest deadline first)
        const dateComparison = dayjs(a.deadline).isBefore(dayjs(b.deadline)) ? -1 : 1;
        
        // Third priority: if dates are the same, prioritize important tasks
        if (dayjs(a.deadline).isSame(dayjs(b.deadline), 'day')) {
          if (a.priority === 'important' && b.priority !== 'important') return -1;
          if (a.priority !== 'important' && b.priority === 'important') return 1;
          return 0;
        }
        
        return dateComparison;
      });
    
    sortedTasksForWhatsApp.forEach((task, index) => {
       const isOverdue = dayjs(task.deadline).isBefore(dayjs(), 'day');
       const importantEmoji = task.priority === 'important' ? '❗' : '';
       const overdueEmoji = isOverdue ? '⚠️' : '';
       const deadlineText = dayjs(task.deadline).format('ddd: DD-MMM YYYY');
       
       message += `📌 ${overdueEmoji}${importantEmoji}${task.text}\n`;
       message += `By ${deadlineText}\n\n`;
     });
    
    message += `Total ${tasks.length} tasks`;
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: whatsappUrl }).catch(err => console.error('Browser open error:', err));
    } else {
      window.open(whatsappUrl, '_blank');
    }
  };

  const getPriorityColor = (priority) => {
    return priority === 'important' ? 'error' : 'default';
  };

  const getPriorityIcon = (priority) => {
    return priority === 'important' ? '❗' : '';
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    const aIsOverdue = dayjs(a.deadline).isBefore(dayjs(), 'day');
    const bIsOverdue = dayjs(b.deadline).isBefore(dayjs(), 'day');
    
    // First priority: overdue tasks come first
    if (aIsOverdue && !bIsOverdue) return -1;
    if (!aIsOverdue && bIsOverdue) return 1;
    
    // Second priority: sort by deadline (nearest deadline first)
    const dateComparison = dayjs(a.deadline).isBefore(dayjs(b.deadline)) ? -1 : 1;
    
    // Third priority: if dates are the same, prioritize important tasks
    if (dayjs(a.deadline).isSame(dayjs(b.deadline), 'day')) {
      if (a.priority === 'important' && b.priority !== 'important') return -1;
      if (a.priority !== 'important' && b.priority === 'important') return 1;
      return 0;
    }
    
    return dateComparison;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ pb: 10 }}>
        
        <Box sx={{ p: 2 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 3, 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}
        >
          任務管理
        </Typography>

        {/* WhatsApp Buttons */}
        {tasks.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => sendAllTasksToWhatsApp('85292892534')}
              sx={{ 
                bgcolor: '#25D366',
                '&:hover': { bgcolor: '#128C7E' },
                borderRadius: 2
              }}
            >
              Send to 🦸🏻
            </Button>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => sendAllTasksToWhatsApp('85262006113')}
              sx={{ 
                bgcolor: '#rgb(255, 144, 207)',
                '&:hover': { bgcolor: 'rgb(252, 140, 185)' },
                borderRadius: 2
              }}
            >
              Send to 🐷
            </Button>
          </Box>
        )}

        {/* Task List */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              📋 我的任務 ({tasks.length})
            </Typography>
            
            {tasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography variant="body1">還沒有任務</Typography>
                <Typography variant="body2">點擊右下角的 + 按鈕新增任務</Typography>
              </Box>
            ) : (
              <List>
                {sortedTasks.map((task, index) => (
                  <React.Fragment key={task.id}>
                    <ListItem
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: task.priority === 'important' ? 'rgba(244, 67, 54, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        border: task.priority === 'important' ? '1px solid rgba(244, 67, 54, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {dayjs(task.deadline).isBefore(dayjs(), 'day') && (
                              <span>⚠️</span>
                            )}
                            {task.priority === 'important' && (
                              <span>❗</span>
                            )}
                            <Typography variant="body1" sx={{ fontWeight: task.priority === 'important' ? 'bold' : 'normal' }}>
                              {task.text}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.secondary">
                              📅 Due：{dayjs(task.deadline).format('ddd, DD-MMM-YYYY')}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                              {dayjs(task.deadline).isBefore(dayjs(), 'day') && (
                                <Chip 
                                  label={`過期 ${dayjs().diff(dayjs(task.deadline), 'day')} 天`} 
                                  size="small" 
                                  color="error" 
                                />
                              )}
                              {dayjs(task.deadline).isSame(dayjs(), 'day') && (
                                <Chip label="今天" size="small" color="warning" />
                              )}
                              <Button
                                size="small"
                                startIcon={<CalendarTodayIcon />}
                                onClick={() => addToGoogleCalendar(task)}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                日曆
                              </Button>
                            </Box>
                          </React.Fragment>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => openEditDialog(task)} 
                          sx={{ mr: 0.5, p: 0}}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          onClick={() => deleteTask(task.id)} 
                          color="error"
                          sx={{ p: 0 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < sortedTasks.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Paper>

        {/* Add Task FAB */}
        <Fab
          color="primary"
          aria-label="add task"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            background: 'linear-gradient(45deg, #667eea, #764ba2)'
          }}
        >
          <AddIcon />
        </Fab>

        {/* Add/Edit Task Dialog */}
        <Dialog open={openDialog} onClose={resetForm} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTask ? '編輯任務' : '新增任務'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <TextField
                fullWidth
                label="任務內容"
                value={taskText}
                onChange={(e) => setTaskText(e.target.value)}
                multiline
                rows={3}
                placeholder="輸入您的任務..."
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">重要：</Typography>
                <IconButton
                  onClick={() => setPriority(priority === 'important' ? 'normal' : 'important')}
                  size="small"
                  sx={{
                    color: priority === 'important' ? '#f44336' : '#9e9e9e',
                    '&:hover': {
                      backgroundColor: priority === 'important' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(158, 158, 158, 0.1)'
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.2rem'
                    }
                  }}
                >
                  {priority === 'important' ? '❗' : '❕'}
                </IconButton>
              </Box>
              
              <DatePicker
                label="截止日期"
                value={deadline}
                onChange={(newValue) => setDeadline(newValue)}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: '提醒時間將設定為當天中午12點'
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm}>取消</Button>
            <Button 
              onClick={editingTask ? editTask : addTask} 
              variant="contained"
              sx={{ background: 'linear-gradient(45deg, #667eea, #764ba2)' }}
            >
              {editingTask ? '更新' : '新增'}
            </Button>
          </DialogActions>
        </Dialog>


        </Box>
      </Box>
    </LocalizationProvider>
  );
}

export default AddTask;